"use client";

/**
 * Guest route wrapper — redirects to /dashboard if already authenticated.
 * Client-side complement to the middleware (handles SPA navigations).
 *
 * Skips the redirect for pages that legitimately need to run for
 * authenticated users despite living under the (auth) layout:
 *   - ``/password/change``, ``/mfa/setup``: enforcement flows.
 *   - ``/auth/callback/microsoft``: SSO admin-consent return. The page
 *     itself decides whether to bounce a same-browser CA back to
 *     /settings or to show a "tenant connected, close this tab" card
 *     for a separate IT admin who has no MI session. Either path
 *     requires the page to actually mount and run its effect — bouncing
 *     authenticated users away here would skip ``finalizeConnect``,
 *     leaving the customer's tenant un-persisted.
 */

import { usePathname, useRouter } from "next/navigation";
import { useEffect, type ReactNode } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "./context";

const NON_GUEST_EXEMPT_PATHS = new Set([
  "/password/change",
  "/mfa/setup",
  "/auth/callback/microsoft",
]);

export function GuestRoute({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  const isEnforcementPage = NON_GUEST_EXEMPT_PATHS.has(pathname);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isEnforcementPage) {
      router.replace("/my-dashboard");
    }
  }, [isLoading, isAuthenticated, isEnforcementPage, router]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated && !isEnforcementPage) {
    return null;
  }

  return <>{children}</>;
}
